<script setup lang="ts">
  import { ref, computed, toRef } from "vue";
  import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
  import { Checkbox } from "@/components/ui/checkbox";
  import SearchInput from "@/components/episto-ui/search-input/SearchInput.vue";
  import { EmptyState } from "@/components/episto-ui/empty-state";
  import { renderMarkdownInline } from "@utils/renderMarkdown";
  import { removeHtmlMarkdownAndLineBreak } from "@utils/toolBox";
  import {
    useFilterableQuestions,
    type FilterableCard,
    type FilterGroup,
    type FilterLeaf,
  } from "@/composables/useFilterableQuestions";

  type Filters = Record<string, Record<string, boolean>>;

  const props = defineProps<{
    modelValue: Filters;
    cards: FilterableCard[];
  }>();

  const emit = defineEmits<{
    "update:modelValue": [value: Filters];
    toggle: [questionId: string, code: string, value: boolean];
  }>();

  const { filterGroups } = useFilterableQuestions(toRef(props, "cards"));

  const search = ref("");

  function normalize(text: string): string {
    return removeHtmlMarkdownAndLineBreak(text || "")
      .toLowerCase()
      .trim();
  }

  const visibleGroups = computed<FilterGroup[]>(() => {
    const term = normalize(search.value);
    if (!term) return filterGroups.value;
    return filterGroups.value.filter((group) => {
      if (normalize(group.label).includes(term)) return true;
      if ((group.children || []).some((leaf) => normalize(leaf.label).includes(term))) return true;
      return group.choices.some((choice) => normalize(choice.label).includes(term));
    });
  });

  // Auto-expand matches while searching; free navigation otherwise.
  const expanded = ref<string[]>([]);
  const expandedModel = computed<string[]>({
    get: () => (normalize(search.value) ? visibleGroups.value.map((g) => g.key) : expanded.value),
    set: (value) => {
      expanded.value = value;
    },
  });

  function isChecked(questionId: string, code: string): boolean {
    return !!props.modelValue?.[questionId]?.[code];
  }

  function activeCount(group: FilterGroup): number {
    const questionIds = group.questionId
      ? [group.questionId]
      : (group.children || []).map((leaf) => leaf.questionId);
    return questionIds.reduce(
      (sum, qId) => sum + Object.values(props.modelValue?.[qId] || {}).filter(Boolean).length,
      0,
    );
  }

  function leafActiveCount(leaf: FilterLeaf): number {
    return Object.values(props.modelValue?.[leaf.questionId] || {}).filter(Boolean).length;
  }

  function toggle(questionId: string, code: string) {
    const next: Filters = { ...(props.modelValue || {}) };
    const codes = { ...(next[questionId] || {}) };
    const value = !codes[code];
    if (value) {
      codes[code] = true;
    } else {
      delete codes[code];
    }
    if (Object.keys(codes).length > 0) {
      next[questionId] = codes;
    } else {
      delete next[questionId];
    }
    emit("update:modelValue", next);
    emit("toggle", questionId, code, value);
  }
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col gap-3">
    <SearchInput
      v-model="search"
      size="sm"
      full-width
      placeholder="Search questions..." />

    <div class="min-h-0 flex-1 overflow-y-auto pr-1">
      <EmptyState
        v-if="!visibleGroups.length"
        title="No matching question."
        size="sm" />

      <Accordion
        v-model="expandedModel"
        type="multiple"
        class="w-full">
        <AccordionItem
          v-for="group in visibleGroups"
          :key="group.key"
          :value="group.key">
          <AccordionTrigger class="min-w-0 gap-2 py-2.5 text-xs">
            <span class="flex min-w-0 flex-1 items-center gap-2">
              <span
                class="min-w-0 truncate"
                :class="activeCount(group) > 0 ? 'font-semibold text-foreground' : ''"
                :title="removeHtmlMarkdownAndLineBreak(group.label)"
                v-html="renderMarkdownInline(group.label)" />
              <span
                v-if="activeCount(group) > 0"
                class="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-foreground px-1 text-3xs font-semibold tabular-nums text-background">
                {{ activeCount(group) }}
              </span>
            </span>
          </AccordionTrigger>
          <AccordionContent class="pb-3">
            <!-- Plain card: direct choice list -->
            <div
              v-if="group.questionId"
              class="flex flex-col gap-1">
              <label
                v-for="choice in group.choices"
                :key="choice.value"
                class="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 text-xs hover:bg-muted"
                :class="isChecked(group.questionId, choice.value) ? 'font-medium text-foreground' : ''">
                <Checkbox
                  :model-value="isChecked(group.questionId, choice.value)"
                  @update:model-value="toggle(group.questionId, choice.value)" />
                <span
                  class="min-w-0 truncate"
                  :title="removeHtmlMarkdownAndLineBreak(choice.label)"
                  v-html="renderMarkdownInline(choice.label)" />
              </label>
            </div>

            <!-- Matrix items / loop iterations: nested sections -->
            <div
              v-else
              class="flex flex-col gap-2">
              <details
                v-for="leaf in group.children"
                :key="leaf.questionId"
                class="group/leaf rounded-md border bg-muted/20 px-2 py-1.5">
                <summary class="flex cursor-pointer list-none items-center gap-2 text-xs font-medium">
                  <span
                    class="min-w-0 flex-1 truncate"
                    :class="leafActiveCount(leaf) > 0 ? 'font-semibold text-foreground' : ''"
                    :title="removeHtmlMarkdownAndLineBreak(leaf.label)"
                    v-html="renderMarkdownInline(leaf.label)" />
                  <span
                    v-if="leafActiveCount(leaf) > 0"
                    class="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-foreground px-1 text-3xs font-semibold tabular-nums text-background">
                    {{ leafActiveCount(leaf) }}
                  </span>
                </summary>
                <div class="mt-1 flex flex-col gap-1">
                  <label
                    v-for="choice in group.choices"
                    :key="choice.value"
                    class="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 text-xs hover:bg-muted"
                    :class="isChecked(leaf.questionId, choice.value) ? 'font-medium text-foreground' : ''">
                    <Checkbox
                      :model-value="isChecked(leaf.questionId, choice.value)"
                      @update:model-value="toggle(leaf.questionId, choice.value)" />
                    <span
                      class="min-w-0 truncate"
                      :title="removeHtmlMarkdownAndLineBreak(choice.label)"
                      v-html="renderMarkdownInline(choice.label)" />
                  </label>
                </div>
              </details>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  </div>
</template>
